import { Injectable } from '@angular/core';
import { NetworkService } from '../network.service';
import { defer, from, Observable, of } from 'rxjs';
import { catchError, delay, first, repeat, switchMap } from 'rxjs/operators';
import { ScannerApiService } from '../scanner/scanner-api.service';
import { ConnectScannerStatus } from '../../interfaces/scan-nutrition';
import { BiozoomError } from '../../interfaces/scanner-api/biozoom-error';
import { ScannerStatus } from '../../interfaces/scanner-api/scanner-status';
import { ApiBasicService } from '../api/api-basic.service';
import { ApiTopService } from '../api/api-top.service';
import { AppConfiguration, CurrentSettings, State, StateInterface } from '../../interfaces/biozoom-api/app-configuration';
import { Scanner } from '../../interfaces/scanner-api/scanner';
import {PipelineStageConfig, StatusObject} from '../../interfaces/biozoom-api/pipeline';
import {getScannerSCNModelFromSerialNumber, isVersionEqualOrNewer} from '../../methods/helpers';
import {logError} from '../../methods/error';

@Injectable({
  providedIn: 'root'
})
export class ScanGenericService {

  constructor(
    private apiBasicService: ApiBasicService,
    private apiTopService: ApiTopService,
    private networkService: NetworkService,
    private scannerApiService: ScannerApiService,
  ) { }

  connectToScanner = (
    setState: ((key: State, val: any) => void),
    setStep: ((data: Record<string, any>) => void),
    scanner: Scanner | { serial?: string },
    state: StateInterface,
    validateSerial: boolean = true,
  ): Observable<{ status: boolean, serial?: string, error?: string }> => {
    console.log('started');
    // defer to delay execution
    // otherwise, a promise would be started immediately and not wait for the
    // previous step to execute
    return defer(() => from(this.scannerApiService.connectToScanner(state, setState))).pipe(
      switchMap((connected) => {
        if (connected[0]) {
          return of({}).pipe(
            switchMap(async () => {
              let r: string;
              // todo: Maybe repeat this request?
              if (connected[1] === '') {
                r = await this.scannerApiService.getIdent(state).toPromise();
              } else {
                r = connected[1];
              }
              // store scanner model to persistentValues
              setState(State.SCANNER_MODEL, getScannerSCNModelFromSerialNumber(r));
              if (!validateSerial) {
                const tempState = {...state};
                tempState.SCANNER_SERIAL = r;
                if (connected[2] && connected[2] > 0) {
                  tempState.SCANNER_VERSION = connected[2];
                }
                let status: ScannerStatus;
                for (let i = 0; i < 5; i++) {
                  status = await this.scannerApiService.getStatus(tempState).toPromise();
                  if (status && Object.keys(status).length > 0) {
                    break;
                  }
                  await new Promise(a => setTimeout(a, 100));
                }
                if (status) {
                  setState(State.CONNECTION_TYPE, 'USB');
                  setState(State.USB_MODE, true);
                  setState(State.SCANNER_SERIAL, r);
                  if (connected[2] && connected[2] > 0) {
                    setState(State.SCANNER_VERSION, connected[2]);
                  }
                  setStep({scanner: status});
                }
              } else if (
                  scanner?.serial !== undefined
                  && scanner?.serial !== null
                  && scanner?.serial !== r
                  && !state.DEMO_MODE
                ) {
                  throw new Error('ERROR');
              }
              return { status: true, serial: r };
            }),
            catchError((err) => {
              if (typeof err === 'object') {
                logError(err);
              }
              console.log(`Caught error ${err.toString()}!`);
              return of({ status: false, error: BiozoomError.SCANNER });
            })
          );
        } else {
          return of({ status: false, error: state.CONNECTION_TYPE === 'USB' ? BiozoomError.USB : BiozoomError.WIFI });
        }
      })
    );
  }

  checkScannerFirmware = (
    scannerStatus: ScannerStatus | {}, usbMode: boolean, config: AppConfiguration
  ): Observable<ConnectScannerStatus> => {
    const statusOk = isVersionEqualOrNewer(scannerStatus, config);
    if (['ok', 'unknown'].includes(statusOk)) {
      if (this.apiBasicService.checkForValidProduct(scannerStatus) || usbMode) {
        return of({ status: true, error: '', restart: false });
      } else {
        return of({ status: false, error: BiozoomError.PRODUCT, restart: true });
      }
    } else {
      if ('wifi_firmware' in scannerStatus && scannerStatus.wifi_firmware) {
        const version = scannerStatus.wifi_firmware.split('.');
        if ((version[0] ?? 0) < 10 && usbMode) {
          return of({ status: true, error: '', restart: false });
        }
      }
      return of({ status: false, error: BiozoomError.OUTDATED, restart: false });
    }
  }
  getStatus = (
    scanner: Scanner,
    state: StateInterface,
    setState: ((key: State, val: any) => void),
    repeats = 5
  ): Observable<ScannerStatus | {}> => {
    return of({}).pipe(
      switchMap(async () => {
        let r: ScannerStatus;
        for (let i = 0; i < repeats; i++) {
          r = await this.scannerApiService.getStatus(state).toPromise();
          if (r && Object.keys(r).length > 0) {
            break;
          }
          await new Promise(a => setTimeout(a, 100));
        }
        if (r && Object.keys(r).length > 0) {
          setState(State.SCANNER_STATUS, r);
        } else {
          r = state?.SCANNER_STATUS ?? {};
        }
        return r;
      }),
      first(),
    );
    // return this.scannerApiService.getStatus(scanner.serial, state);
  }

  startHandRecognition = (state: StateInterface, settings: CurrentSettings, wifiFirmware?: string): Observable<boolean> => {
    return this.scannerApiService.getHandrecognition(state, settings, false, wifiFirmware).pipe(
      delay(500),
      repeat(),
      switchMap((handOn) => {
        if (handOn) {
          return of(true);
        } else {
          return of(false);
        }

      })
    );
  }


  startHandOffRecognition = (state: StateInterface, settings: CurrentSettings, wifiFirmware?: string): Observable<boolean> => {
    return this.scannerApiService.getHandrecognition(state, settings, true, wifiFirmware).pipe(
      delay(500),
      repeat(),
      switchMap((handOff) => {
        if (handOff) {
          return of(true);
        } else {
          return of(false);
        }

      })
    );
  }

  disconnectAndConnectToServer = (state: StateInterface) => {
    return defer(() => from(this.scannerApiService.disconnectScanner(state)));
    // return defer(() => from(this.networkService.disconnectScanner(state)));
    //   .pipe(
    //   switchMap(() => {
    //     return this.networkService.waitForServerConnection(state, 30000);
    //   })
    // );
  }

  fetchResult = (
    demoMode: boolean,
    language: string,
    serial: string,
    additionalData?: any,
    preRequestTimeoutMs?: number,
  ) => {
    return this.apiTopService.executeMergeAoxResults(
      demoMode,
      serial,
      language,
      additionalData,
      preRequestTimeoutMs,
    );
  }

  requestPreciseAoxUploadServerOnServer = (
    demoMode: boolean,
    serial: string,
    vwsBaseUrl: string,
    preRequestTimeoutMs?: number,
  ) => this.apiTopService.requestMergeAoxVWSOnServer(demoMode, serial, vwsBaseUrl, preRequestTimeoutMs);

  uploadPreciseAoxToSelectedServer = (
    demoMode: boolean,
    serial: string,
    waitingTimeMs: number,
    recommendedServer: string,
    language: string,
    additionalData: { series_q: number[], series_results: number[] },
  ) => this.apiTopService.executeMergeAoxResultsOnServer(
    demoMode, serial, waitingTimeMs, recommendedServer, language, additionalData
  );

  getAoxUploadServers = (): string[] => this.apiTopService.getAoxVwsServers();

  deleteFlash = async (pipelineConfig: PipelineStageConfig) => {
    return this.scannerApiService.deleteFlash(pipelineConfig);
  }

  fetchSerial = (pipelineConfig: PipelineStageConfig) => {
    // tslint:disable-next-line:variable-name
    return switchMap(async (_stati: StatusObject): Promise<Observable<any>> => {
      pipelineConfig.setBlockScreen(true);
      const [_C, serial, _V] = await this.scannerApiService.connectToScanner(
        pipelineConfig.state.persistentValues, pipelineConfig.setState
      );
      if (!serial || serial === '') {
        pipelineConfig.setBlockScreen(false);
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of('');
      } else {

      }
      const resKey = pipelineConfig.stageConfig.resKey ?? '';
      const resGroup = pipelineConfig.stageConfig.resGroup ?? '';
      pipelineConfig.setState(resKey, serial, resGroup);
      pipelineConfig.setState(State.CONNECTION_TYPE, 'USB');
      pipelineConfig.setState(State.USB_MODE, true);
      pipelineConfig.setBlockScreen(false);
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(serial);
    });
  }

  finalizeScanner(pipelineConfig: PipelineStageConfig) {
    return switchMap(async () => {
        const res = await this.scannerApiService.finalizeScanner(pipelineConfig);
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return of(true);
      }
    );
  }
}
